import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { CustomPeriodCard } from '@/pages/Overview/CustomPeriodCard';
import { useStudyYearPeriod } from '@/pages/Overview/hooks/useStudyYearPeriod';
import { isSamePeriod } from '@/pages/Overview/period';
import { PeriodCard } from '@/pages/Overview/PeriodCard';
import {
  BorderRadius,
  ButtonVariant,
  ModalSize,
  ModalVariant,
  Size,
} from '@/types/components';
import { StudyYearPeriodType, type StudyYearPeriod } from '@/types/overview';

interface Props {
  periods: StudyYearPeriod[];
  value: StudyYearPeriod;
  onClose: () => void;
  onApply: (period: StudyYearPeriod) => void;
}

export const PeriodSelectSheet: React.FC<Props> = ({
  periods,
  value,
  onClose,
  onApply,
}) => {
  const { t } = useTranslation();
  const studyYearPeriod = useStudyYearPeriod();
  const [draft, setDraft] = useState<StudyYearPeriod>(value);
  const { sheetRef, swipeProps } = useSwipeToClose(onClose);

  const isCustomPeriod = draft.type === StudyYearPeriodType.Custom;

  return (
    <Modal
      isOpen
      label={t('overview.attendance.period.selectTitle')}
      variant={ModalVariant.BottomSheet}
      size={ModalSize.Small}
      className="max-h-[93svh] min-h-[72svh]"
      contentRef={sheetRef}
      onClose={onClose}
    >
      <div
        {...swipeProps}
        className="flex shrink-0 cursor-grab flex-col touch-none active:cursor-grabbing"
      >
        <div className="flex justify-center pt-2 pb-1">
          <div className="h-1 w-10 rounded-full bg-drag-handle" />
        </div>
        <div className="flex items-center px-4 pt-3 pb-3">
          <h2 className="text-[24px]/[30px] font-semibold text-foreground">
            {t('overview.attendance.period.selectTitle')}
          </h2>
        </div>
      </div>

      <ul className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-4 pb-2">
        {periods.map((period) => (
          <li key={period.name}>
            <PeriodCard
              period={period}
              isSelected={isSamePeriod(period, draft)}
              onSelect={setDraft}
            />
          </li>
        ))}
        <li>
          <CustomPeriodCard
            studyYearPeriod={studyYearPeriod}
            value={isCustomPeriod ? draft : null}
            onChange={setDraft}
          />
        </li>
      </ul>

      <div className="shrink-0 px-4 pt-3 pb-[calc(16px+env(safe-area-inset-bottom))]">
        <Button
          variant={ButtonVariant.Primary}
          size={Size.Medium}
          radius={BorderRadius.Small}
          className="h-14 w-full font-semibold"
          onClick={() => onApply(draft)}
        >
          {t('overview.ratings.save')}
        </Button>
      </div>
    </Modal>
  );
};
